import React, { useEffect, useState } from 'react';
import { downloadACPdocument, getLogs } from '@api/resident';
import { ApiResponse, ListApiResponse } from '@typings/ApiResponse';
import { LastedUpdateDateData } from '@typings/ResidentTypes';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import ImportantNotificationBanner from '@components/ImportantNotificationBanner';

interface DownloadPrintShareProps {
  slug: string | undefined;
  userId: string;
  fullName: string | undefined;
}
const DownloadPrintShare = (props: DownloadPrintShareProps) => {
  const { slug, userId, fullName } = props;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  useEffect(() => {
    const handleGetLogs = async () => {
      if (userId) {
        try {
          const res: ApiResponse<ListApiResponse<LastedUpdateDateData>> =
            await getLogs(userId, 1, 5);
          if (res && res.status === 'success') {
            const updatedAt = res.data?.data?.[0]?.updatedAt;
            if (updatedAt) {
              const formatted = new Date(updatedAt).toISOString().split('T')[0];
              setLastUpdatedAt(formatted);
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
    handleGetLogs();
  }, [userId]);

  const handleDownloadPDF = async () => {
    try {
      if (!slug) return;

      setIsDownloading(true);
      setDownloadSuccess(false);

      const response = await downloadACPdocument(userId, slug);

      if (!response) {
        throw new Error('No data received from server');
      }

      // Convert response into a blob
      const url = window.URL.createObjectURL(new Blob([response]));

      // Create a temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = lastUpdatedAt
        ? `${fullName} - AdvanceCarePlan - ${lastUpdatedAt}.pdf`
        : `${fullName} - AdvanceCarePlan.pdf`; // filename shown to user
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      setErrorMessage(null);
      setDownloadSuccess(true); // ✅ show success message
    } catch (error: any) {
      console.error('Download failed:', error);
      if (error?.response?.status === 404) {
        setErrorMessage(
          'Please enter more information before downloading this Advance Care Plan'
        );
      } else {
        setErrorMessage(
          error?.message ||
            'Something went wrong while downloading the PDF. Please try again.'
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="download-print mt-10 govuk-bp:mt-14">
      <h1 className="govuk-heading-l">
        Download and print this Advance Care Plan
      </h1>
      <p className="govuk-body-s">
        Download and print this Advance Care Plan using the buttons below If the
        ‘Print’ button doesn’t automatically connect with your printer please
        use the ‘Download’ button. Once downloaded (as a PDF) print the document
        now saved on your device.
      </p>
      {errorMessage && (
        <div
          className="govuk-error-message"
          style={{ marginTop: '1rem', color: 'red' }}
        >
          {errorMessage}
        </div>
      )}
      <div className="govuk-button-group">
        <button
          type="submit"
          className="govuk-button govuk-!-width-one-half"
          data-module="govuk-button"
          onClick={handleDownloadPDF}
        >
          Download
        </button>
        <button
          type="submit"
          className="govuk-button govuk-button--secondary govuk-!-width-one-half"
          data-module="govuk-button"
        >
          Print
        </button>
      </div>
      {/* {sharingPrintedCopiesStatus.submitted === SubmitStatusEnum.SUCCESS && (
        <SuccessNotificationBanner
          title={sharingPrintedCopiesStatus.message}
          duration={3000}
          noScrollToTop
        />
      )} */}

      {/* ✅ Status messages */}
      {isDownloading && (
        <ImportantNotificationBanner
          title=" Downloading your PDF, please wait..."
          duration={10000}
          noScrollToTop
        />
      )}
      {downloadSuccess && !isDownloading && (
        <SuccessNotificationBanner
          title="Your PDF has been downloaded successfully."
          duration={10000}
          noScrollToTop
        />
      )}
    </div>
  );
};

export default DownloadPrintShare;
